import React, { useEffect, useState } from 'react';
import formatBalance from '../../../helpers/format-balance';
import { getAccountIcon, getAccountName, getShowDecimal } from '../../../helpers/account-types';
import { decode } from '../../../helpers/encode-decode';
import { Link, Redirect } from "react-router-dom";
import { padLeft } from '../../../helpers/pad';
import Loader from "../../../components/loader";
import Picture from "../../../components/picture";

import { getUser, suggestContacts, getContact, credit, debit } from "../../../api";
import { ReactComponent as ArrowBackIcon } from "../../../images/icons/arrow_back_black_24dp.svg";
import { ReactComponent as ExpandMoreIcon } from "../../../images/icons/expand_more_black_24dp.svg";
import { ReactComponent as TaskAltIcon } from "../../../images/icons/task_alt_black_24dp.svg";
import { ReactComponent as QRCodeScannerIcon } from "../../../images/icons/qr_code_scanner_black_24dp.svg";
import { ReactComponent as CancelIcon } from "../../../images/icons/cancel_black_24dp.svg";
import { ReactComponent as CameraSwitchIcon } from "../../../images/icons/cameraswitch_black_24dp.svg";
import formatDate from "../../../helpers/format-date";

const ERROR_MESSAGES = {
  "INVALID_INPUT": "Données invalides.",
  "SOURCE_NOT_FOUND": "Le compte source n'a pas été trouvé.",
  "DESTINATION_NOT_FOUND": "Le compte de destination n'a pas été trouvé.",
  "NOT_SAME_CURRENCY": "Les comptes n'utilisent pas la même devise.",
  "INVALID_AMOUNT": "Montant invalide.",
  "INSUFFICIENT_FUNDS": "Pas assez de fonds.",
  "DIFFERENT_SOURCE_DESTINATION": "La source et la destination doivent être différents.",
  "INSERT_ERROR": "Erreur inconnue.",
  "INVALID_QR": "Code QR invalide.",
};

let timeout;

function CreditDebit() {
  const [user, setUser] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [scanning, setScanning] = useState(null);

  // 1. Suggestion
  const [suggestionTerm, setSuggestionTerm] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  // 2. Contact
  const [accounts, setAccounts] = useState(null);
  const [contact, setContact] = useState(null);
  // 3. Account
  const [selectedAccount, setSelectedAccount] = useState(null);
  // 4. Amount
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [selectedAmountString, setSelectedAmountString] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  // 5. Description
  const [selectedDescription, setSelectedDescription] = useState("");

  // Validation
  const [validationScreen, setValidationScreen] = useState(false);

  // Transaction + confirmation
  const [doingTransaction, setDoingTransaction] = useState(false);
  const [transactionError, setTransactionError] = useState(null);
  const [transactionResult, setTransactionResult] = useState(null);

  useEffect(() => {
    Promise.all([
      getUser().then(user => () => setUser(user)),
      new Promise(accept => setTimeout(() => accept(() => {}), 1000)),
    ]).then(cbs => cbs.forEach(cb => cb()));
  }, []);

  const [accountSelectionOpened, setAccountSelectionOpened] = useState(false);
  const closeAccountSelection = () => {
    document.body.removeEventListener("click", closeAccountSelection);
    setAccountSelectionOpened(false);
  }
  const openAccountSelection = () => {
    if(!accountSelectionOpened) {
      setTimeout(() => document.body.addEventListener("click", closeAccountSelection), 0);
      setAccountSelectionOpened(true);
    }
  }

  if(!user) {
    return (
      <div className="Transfer">
        <Loader />
      </div>
    );
  }

  if(user.rights < 1) {
    // TODO : Make an unauthorized component ?
    return <Redirect to="/" />;
  }

  const account = selectedAccount && accounts && accounts.find(a => a.id === selectedAccount);
  const AccountIcon = getAccountIcon(account && account.type);
  const selectedAmountFormatted = selectedAmount === null ? "" : (account ? formatBalance(selectedAmount, getShowDecimal(account.type), account.currency_symbol) : "" + formatBalance(selectedAmount, false, ""));

  const handleSuggestContacts = (c) => {
    setSuggestionTerm(c);
    if(c) {
      if(timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        suggestContacts(c).then(suggestions => setSuggestions(suggestions));
      }, 1000);
    } else {
      setSuggestions(null);
    }
  };

  const selectContact = (c, no) => {
    if(c) {
      setSuggestions(null);
      getContact(c).then(infos => {
        // TODO : Show error if no contact ? (wrong QR for example)
        setContact(infos.contact);
        setSuggestionTerm(infos.contact ? infos.contact.login : "");
        setAccounts(infos.accounts);
        if(infos.accounts.length) {
          if(no && infos.accounts.find(a => a.id === no)) {
            setSelectedAccount(no);
          } else {
            setSelectedAccount(infos.accounts[0].id);
          }
        } else {
          setSelectedAccount(null);
        }
      });
    }
  };

  const setAmount = (e) => setSelectedAmountString(("" + e.target.value).replace(/[^0-9.,]/g, ""));
  const onFocus = () => {
    setSelectedAmountString(selectedAmount === null ? "" : ("" + (selectedAmount / 100)));
    setInputFocused(true);
  }
  const onBlur = () => {
    let value = parseFloat(selectedAmountString.replace(/,/g, "."));

    if(isNaN(value)) {
      setSelectedAmount(null);
      return;
    }

    value = Math.floor(value * 100);

    // Remove decimal part if not allowed
    if(!getShowDecimal(account.type)) {
      value = Math.floor(value / 100) * 100;
    }

    // Amount must be greater than 0
    if(value < 0) {
      return;
    }

    // Amount can't exceed balance
    if(account && selectedTab === 1) {
      value = Math.min(value, account.balance);
    }

    setSelectedAmount(value);
    setInputFocused(false);
  }

  // Confirmation page
  if(transactionResult) {
    return (
      <div className="Transfer">
        <div className="Transfer-header">
          <div className="Transfer-header-title">
            <Link to="/">
              <ArrowBackIcon fill="#FFFFFF" width="32px" height="32px" />
            </Link>
            <div className="Transfer-header-title-text">Transaction effectuée</div>
          </div>
          <div className="Transfer-confirmation">
            <div className="Transfer-confirmation-logo">
              <TaskAltIcon fill="#FFFFFF" width="128px" height="128px" />
            </div>
            <div className="Transfer-confirmation-result">{selectedTab === 0 ? "Crédit" : "Débit"} effectué.</div>
            {!!transactionResult.code && (
              <div className="Transfer-confirmation-text">Votre code de validation est le {transactionResult.code}.</div>
            )}
          </div>
          <Link className="Account-header-action-link" to="/">
            <div className={"Transfer-button"}>Retour</div>
          </Link>
        </div>
      </div>
    );
  }

  // Validation page
  if(validationScreen) {
    const sendTransaction = () => {
      setDoingTransaction(true);
      const doTransaction = selectedTab === 0 ? credit : debit;
      Promise.all([
        doTransaction(selectedAccount, selectedAmount, selectedDescription).catch(error => ({ error })),
        new Promise(accept => setTimeout(() => accept(() => {}), 1500)),
      ]).then(([result]) => {
        setDoingTransaction(false);
        if(result && result.success) {
          setTransactionResult(result);
        } else if (result && result.error) {
          setTransactionError(result.error);
        }
      });
    };

    return (
      <div className="Transfer">
        <div className="Transfer-header">
          <div className="Transfer-header-title">
            <div className="Transfer-header-title-icon" onClick={() => setValidationScreen(false)}>
              <ArrowBackIcon fill="#FFFFFF" width="32px" height="32px" />
            </div>
            <div className="Transfer-header-title-text">Confirmation du {selectedTab === 0 ? "crédit" : "débit"}</div>
          </div>
          <div className="Transfer-validation">
            <div className="Transfer-validation-contact">
              <div className="Transfer-validation-contact-picture">
                <Picture {...contact} />
              </div>
              <div className="Transfer-validation-contact-name">
                {contact.firstname} {contact.lastname}
              </div>
              {!!contact.email && (
                <div className="Transfer-validation-contact-email">{contact.email}</div>
              )}
            </div>
            <div className="Transfer-validation-date">{selectedTab === 0 ? "Crédit" : "Débit"} le {formatDate(new Date(), "%-d %b %Y")}</div>
            <div className="Transfer-validation-amount">{selectedTab === 0 ? "+" : "-"}{selectedAmountFormatted}</div>
            {!!selectedDescription && (
              <div className="Transfer-validation-description">{selectedDescription}</div>
            )}
            <div className="Transfer-validation-action">
              {selectedTab === 0 ? "Crédit au compte" : "Débit au compte"}
            </div>
          </div>
          {!!transactionError && (
            <div className="Transfer-validation-error">
              {(transactionError.code && ERROR_MESSAGES[transactionError.code]) || transactionError.error || "Erreur inconnue."}
            </div>
          )}
          <div className={"Transfer-button"} onClick={sendTransaction}>Envoyer</div>
        </div>
        {doingTransaction && (
          <div className="Transfer-loader">
            <Loader text={"Transfert en cours..."} goingOut />
          </div>
        )}
      </div>
    );
  }

  const detectCameras = () => {
    window.Html5Qrcode.getCameras().then(cameras => {
      if (cameras && cameras.length) {
        const devices = [];
        cameras.forEach(d => {
          if(d.label.includes(",")) {
            const name = d.label.replace(/.*,/, "");
            if(devices.find(d2 => d2.id !== d.id && d2.label.includes(name))) {
              return;
            }
          }
          devices.push(d);
        });
        const cameraId = devices[0].id;

        setScanning({ cameraId, devices });
      }
    }).catch(_ => {
      // handle err
    });
  };

  const processQR = html5QrCode => qrCodeMessage => {
    try {
      const contactData = JSON.parse(atob(decode(qrCodeMessage)));
      html5QrCode.stop();
      setScanning({ contactData: {
        n: contactData.n,
        o: contactData.o,
        firstname: contactData.f,
        lastname: contactData.l,
        email: contactData.e,
        login: contactData.u,
        picture: contactData.p,
      }});
    } catch(e) {
      setScanning(state => ({ ...state, error: "INVALID_QR" }));
      setTimeout(() => setScanning(state => ({ ...state, error: undefined })), 2500);
    }
  };

  const switchCamera = () => {
    if(scanning && scanning.html5QrCode) {
      scanning.html5QrCode.stop().then(_ => {
        const deviceIndex = scanning.devices.findIndex(d => d.id === scanning.cameraId);
        const cameraId = scanning.devices[(deviceIndex + 1)%scanning.devices.length].id;
        setScanning(state => ({ ...state, cameraId }));
        scanning.html5QrCode.start(
          cameraId,
          { fps: 10, qrbox: 250 },
          processQR(scanning.html5QrCode),
          _ => {}
        ).catch(err => {
          // Start failed, handle it. For example,
          console.log(`Unable to start scanning, error: ${err}`);
        });
      });
    }
  }

  const startScanning = (e) => {
    if(e && !scanning.html5QrCode) {
      const html5QrCode = new window.Html5Qrcode("reader");
      html5QrCode.start(
        scanning.cameraId,
        { fps: 10, qrbox: 250 },
        processQR(html5QrCode),
        _ => {}
      ).catch(err => {
        // Start failed, handle it. For example,
        console.log(`Unable to start scanning, error: ${err}`);
      });
      setScanning(state => ({ ...state, html5QrCode }));
    }
  }

  const stopScanning = () => {
    if(scanning && scanning.html5QrCode) {
      scanning.html5QrCode.stop();
    }
    setScanning(null);
  }

  // Form page
  return (
    <div className="Transfer">
      <div className="Transfer-header">
        <div className="Transfer-header-title">
          <div className="Transfer-header-title-icon">
            <Link to="/">
              <ArrowBackIcon fill="#FFFFFF" width="32px" height="32px" />
            </Link>
          </div>
          <div className="Transfer-header-title-text">{selectedTab === 0 ? "Crédit" : "Débit"}</div>
        </div>
      </div>
      <div className="Transfer-content">
        <div className="Transfer-tabs">
          <div className={"Transfer-tabs-tab" + (selectedTab === 0 ? " Transfer-tabs-tab-selected" : "")} onClick={() => setSelectedTab(0)}>Crédit</div>
          <div className={"Transfer-tabs-tab" + (selectedTab === 1 ? " Transfer-tabs-tab-selected" : "")} onClick={() => setSelectedTab(1)}>Débit</div>
        </div>
        <div className="Transfer-section">
          <div className="Transfer-section-title">Rechercher un contact</div>
          <div className="Transfer-contact-input">
            <input className="Transfer-input Transfer-input-contact" value={suggestionTerm} onChange={(e) => handleSuggestContacts(e.target.value)} placeholder="Entrez un nom d'utilisateur ou une adresse courriel" />
            <QRCodeScannerIcon className="Transfer-qrcode-button" onClick={detectCameras}/>
            {!!suggestions && (
              <div className="Transfer-account-select-dropdown">
                {!suggestions.length && (
                  <div key="no-suggestion" className="Transfer-account-select-account-info">
                    <div className="Transfer-account-select-account-info-info">
                      Pas de contact trouvé.
                    </div>
                  </div>
                )}
                {suggestions.map(suggestion => {
                  return (
                    <div key={suggestion.login} className="Transfer-account-select-account-info" onClick={() => selectContact(suggestion.login)}>
                      <div className="Transfer-account-select-account-info-picture">
                        <Picture {...suggestion} />
                      </div>
                      <div className="Transfer-account-select-account-info-info">
                        <div className="Transfer-account-select-account-info-balance">{suggestion.firstname} {suggestion.lastname}</div>
                        <div className="Transfer-account-select-account-info-name">{suggestion.login}{suggestion.email ? " - " + suggestion.email : ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {!!contact && (
            <div className="Transfer-contacts">
              <div className="Transfer-account-select-account-info">
                <div className="Transfer-account-select-account-info-picture Transfer-contacts-contact-picture">
                  <Picture {...contact} />
                </div>
                <div className="Transfer-account-select-account-info-info">
                  <div className="Transfer-account-select-account-info-balance">{contact.firstname} {contact.lastname}</div>
                  <div className="Transfer-account-select-account-info-name">{contact.login}{contact.email ? " - " + contact.email : ""}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        {!!contact && (
          <div className="Transfer-section">
            <div className="Transfer-section-title">Compte</div>
            <div className={"Transfer-account-select " + (accountSelectionOpened ? "Transfer-account-select-opened" : "Transfer-account-select-closed")}>
              <div className="Transfer-account-select-selected" onClick={openAccountSelection}>
                {!account && (
                  <div className="Transfer-account-select-selected-placeholder">Veuillez sélectionner un compte</div>
                )}
                {!!account && (
                  <div className="Transfer-account-select-account-info">
                    <div className="Transfer-account-select-account-info-picture">
                      <AccountIcon fill="#108F38" height="48px" width="48px" />
                    </div>
                    <div className="Transfer-account-select-account-info-info">
                      <div className="Transfer-account-select-account-info-name">{getAccountName(account.type)} (n°{padLeft(account.id, 8)})</div>
                      <div className="Transfer-account-select-account-info-balance">{formatBalance(account.balance, getShowDecimal(account.type), account.currency_symbol)}</div>
                    </div>
                  </div>
                )}
                <div className="Transfer-account-select-selected-arrow">
                  <ExpandMoreIcon />
                </div>
              </div>
              {accountSelectionOpened && (
                <div className="Transfer-account-select-dropdown">
                  {accounts.map(account => {
                    const AccountIcon = getAccountIcon(account.type);
                    return (
                      <div key={account.id} className="Transfer-account-select-account-info" onClick={() => { if(account.id !== selectedAccount) { setSelectedAccount(account.id); if(!account || account.currency !== accounts.find(a => a.id === selectedAccount).currency) { setSelectedAmount(null); } } }}>
                        <div className="Transfer-account-select-account-info-picture">
                          <AccountIcon fill="#108F38" height="48px" width="48px" />
                        </div>
                        <div className="Transfer-account-select-account-info-info">
                          <div className="Transfer-account-select-account-info-name">{getAccountName(account.type)} (n°{padLeft(account.id, 8)})</div>
                          <div className="Transfer-account-select-account-info-balance">{formatBalance(account.balance, getShowDecimal(account.type), account.currency_symbol)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {!!contact && (
          <div className="Transfer-section">
            <div className="Transfer-section-title">Montant</div>
            <div className="Transfer-amount-input">
              <input
                className="Transfer-input Transfer-input-amount"
                value={inputFocused ? selectedAmountString : selectedAmountFormatted}
                onChange={setAmount}
                onFocus={onFocus}
                onBlur={onBlur}
                type="text"
                inputMode={getShowDecimal(account && account.type) ? "decimal" : "numeric"}
                placeholder="Entrez un montant à transférer"
              />
            </div>
          </div>
        )}
        {!!contact && (
          <div className="Transfer-section">
            <div className="Transfer-section-title">Description</div>
            <div>
              <input className="Transfer-input" value={selectedDescription} onChange={(e) => setSelectedDescription(e.target.value)} placeholder="Entrez une description (facultatif)" maxLength="50" />
            </div>
          </div>
        )}
        <div className={"Transfer-button" + (!account || !contact || selectedAmount === 0 ? " Transfer-button-disabled" : "")} onClick={() => setValidationScreen(true)}>Valider</div>
      </div>
      {scanning && (
        <div className="Transfer-scanning">
          {!scanning.contactData && (
            <div className="Transfer-scanning-video" id="reader" ref={startScanning} />
          )}
          {!!scanning.contactData && (
            <div className="Transfer-scanning-contact">
              <div className="Transfer-scanning-contact-picture">
                <Picture {...scanning.contactData} />
              </div>
              <div className="Transfer-scanning-contact-name">
                {scanning.contactData.firstname} {scanning.contactData.lastname}
              </div>
              {!!scanning.contactData.email && (
                <div className="Transfer-scanning-contact-email">{scanning.contactData.email}</div>
              )}
              <div className="Transfer-scanning-contact-account">Compte n°{padLeft(scanning.contactData.n, 8)}</div>
            </div>
          )}
          <div className="Transfer-scanning-cancel-buttons">
            {scanning.devices && scanning.devices.length > 1 && (
              <CameraSwitchIcon fill="#FFFFFF" width="32px" height="32px" onClick={switchCamera} />
            )}
            <div style={{ flex: 1 }} />
            <CancelIcon fill="#FFFFFF" width="32px" height="32px" onClick={stopScanning} />
          </div>
          <div className={"Transfer-scanning-validation-buttons" + (scanning.contactData ? "" : " Transfer-scanning-validation-button-disabled")}>
            <div className="Transfer-scanning-button" onClick={() => { selectContact(scanning.contactData.login || scanning.contactData.email, scanning.contactData.n); stopScanning(); }}>Valider</div>
          </div>
          <div className={"Transfer-scanning-error" + (scanning.error ? " Transfer-scanning-error-visible" : "")}>
            <div className="Transfer-scanning-error-content">
              {ERROR_MESSAGES[scanning.error] || scanning.error}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreditDebit;